import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import CardMedia from "@mui/material/CardMedia";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import Switch from "@mui/material/Switch";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";

import EmptySizeChartCard from "pages/de/size-chart/components/EmptySizeChartCard";
import CreateSizeChartModal from "pages/de/size-chart/components/CreateSizeChartModal";
import { Empty } from "antd";

import Error404 from "pages/error/404";

const SizeCharts = ({ sharedView = false }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [createSizeChartVisible, setCreateSizeChartVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  let location = useLocation();
  const navigate = useNavigate();

  const user = useContext(AccountContext);

  const {
    tree,
    actions: { findNodeByID, findParents },
  } = useContext(CategoryContext);

  useEffect(() => {
    retrieveSizeCharts();
  }, []);

  const retrieveSizeCharts = async () => {
    try {
      const shared = await api.sizeChart.sharedList({
        order: "-updated_at",
      });
      let sharedSizeCharts = shared?.data?.sizes || [];
      sharedSizeCharts = sharedSizeCharts.map((x) => ({ ...x, name: "Shared:" + x.name }));

      const allSizeCharts = [...sharedSizeCharts];

      setData(allSizeCharts);
      setFilteredData(allSizeCharts);
    } catch (err) {
      toast.error(`Retrieve size chart list failed!`);
      console.log(err);
    }
  };

  const handleDuplicateSizeChart = async (data = {}) => {
    try {
      const payload = {
        name: "Duplicate of " + data["name"],
        sizes: data["sizes"],
        gender: data["gender"],
        category: data["category"],
        class: data["class"],
        attribute: data["attribute"],
        metadata: data["metadata"],
      };

      const newCreatedSizeChart = await api.sizeChart.create(payload);
      toast.success(`${payload["name"]} has been duplicated successfully!`);
      navigate(`/setup/size-master/${newCreatedSizeChart.data.result.id}`);
    } catch (err) {
      console.log(err);
      toast.error(`Size Chart could not be duplicated!`);
    }
  };

  const getStyleTableRow = (item) => {
    const categorys = item?.attribute?.category || [];
    let name = item?.name;
    if (item?.attribute?.unit?.value) {
      name = name + `(${item?.attribute?.unit?.label})`;
    }

    return {
      key: item.id,
      name: name,
      class: item?.class,
      gender: item?.gender,
      category: categorys.map((x) => x?.label).join(","),
      sizes: item?.sizes,
      created_at: item.created_at,
      updated_at: item.updated_at,
      action: item,
    };
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word", maxWidth: 240 }}>{value}</div>;
      },
    },
    {
      Header: "Class",
      accessor: "class",
    },
    {
      Header: "Gender",
      accessor: "gender",
    },
    {
      Header: "Product Category",
      accessor: "category",
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word" }}>{value}</div>;
      },
    },
    {
      Header: "Sizes",
      accessor: "sizes",
      Cell: ({ row, value }) => {
        return (
          <div
            style={{
              wordBreak: "break-word",
              maxWidth: 180,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      Header: "Last Edit",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "Ownership",
      accessor: "isSharedWithMe",
      Cell: ({ row, value }) => {
        return value ? "Admin" : "You";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <Tooltip title="Import to my size charts">
              <SuiBox>
                <SuiButton
                  variant="text"
                  color="dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateSizeChart(value);
                  }}
                >
                  <DownloadIcon />
                  IMPORT
                </SuiButton>
              </SuiBox>
            </Tooltip>
          </SuiBox>
        );
      },
    },
  ];

  const refresh = () => {
    retrieveSizeCharts();
  };

  const rows = filteredData.map((item) => getStyleTableRow(item));

  const handleRowClick = (row) => {
    navigate(`/shared-with-me/size-master/${row?.original?.key}`);
  };

  return (
    <SuiBox my={1}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={loading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
      </Backdrop>

      <Card sx={{ p: 3 }}>
        {rows.length > 0 ? (
          <DataTable
            table={{ columns, rows: rows }}
            entriesPerPage={{ defaultValue: 20 }}
            noEndBorder
            handleRowClick={handleRowClick}
          />
        ) : (
          <Empty />
        )}
      </Card>
      {createSizeChartVisible ? (
        <CreateSizeChartModal
          handleClose={() => setCreateSizeChartVisible(false)}
          visible={createSizeChartVisible}
          isEdit={false}
          handleRefresh={refresh}
        />
      ) : null}
    </SuiBox>
  );
};

export default SizeCharts;
